import React, { useState, useEffect, useCallback } from 'react';
import { 
    Box, Typography, Container, Grid, Card, CardContent, 
    LinearProgress, Chip, Paper, useTheme, Tooltip
} from '@mui/material';
import { 
    HealthAndSafety as HealthAndSafetyIcon, 
    Storage as StorageIcon, 
    Memory as MemoryIcon,
    Dvr as DvrIcon,
    NetworkPing as NetworkPingIcon,
    CheckCircle as CheckCircleIcon,
    Error as ErrorIcon,
    Warning as WarningIcon
} from '@mui/icons-material';

// --- MOCK DATA GENERATOR (Simulating Real-Time Updates) ---

const generateMetric = (label, min, max, unit, threshold) => {
    // Generate a random value with some bias towards the healthy side (lower usage/latency)
    const value = Math.min(max, (Math.random() * (max - min) + min) * (Math.random() < 0.2 ? 1.5 : 1));
    const statusValue = Math.round(value);

    let status = 'Healthy';
    let color = 'success';
    let icon = CheckCircleIcon;

    if (value >= threshold.warning) {
        status = 'Warning';
        color = 'warning';
        icon = WarningIcon;
    }
    if (value >= threshold.error) {
        status = 'Critical';
        color = 'error';
        icon = ErrorIcon;
    }

    return {
        label,
        value: statusValue,
        unit,
        max,
        status,
        color,
        icon: icon,
    };
};

const getLiveMetrics = () => [
    generateMetric('CPU Utilization', 10, 85, '%', { warning: 75, error: 90 }),
    generateMetric('Memory Usage', 20, 95, '%', { warning: 80, error: 95 }),
    generateMetric('DB Connection Pool', 5, 100, '%', { warning: 85, error: 98 }),
    generateMetric('API Latency', 20, 250, 'ms', { warning: 150, error: 200 }),
    generateMetric('Disk I/O Rate', 5, 75, '%', { warning: 60, error: 80 }),
    generateMetric('Cache Hit Ratio', 70, 99, '%', { warning: 75, error: 70 }), // Note: Lower is worse for ratio
    generateMetric('Request Queue Depth', 0, 40, 'jobs', { warning: 25, error: 35 }),
    generateMetric('Network Throughput', 10, 90, 'Mbps', { warning: 80, error: 95 }),
];

// Map for selecting the correct icon
const metricIcons = {
    'CPU Utilization': DvrIcon,
    'Memory Usage': MemoryIcon,
    'DB Connection Pool': StorageIcon,
    'API Latency': NetworkPingIcon,
    'Disk I/O Rate': StorageIcon,
    'Cache Hit Ratio': MemoryIcon,
    'Request Queue Depth': DvrIcon,
    'Network Throughput': NetworkPingIcon,
};

// --- HELPER COMPONENT: Metric Card ---
const MetricCard = ({ metric, theme }) => {
    const MetricIcon = metricIcons[metric.label] || HealthAndSafetyIcon;
    const progressValue = metric.label === 'Cache Hit Ratio' ? metric.value : metric.value;

    // Determine value used for progress bar. For Cache Hit Ratio, a lower value is 'worse',
    // so the progress bar should reflect the value directly. For usage metrics, the value is the progress.
    const displayProgress = metric.label === 'Cache Hit Ratio' ? metric.value : metric.value;

    return (
        <Card 
            elevation={4} 
            sx={{ 
                height: '100%', 
                borderLeft: `5px solid ${theme.palette[metric.color].main}`, 
                transition: '0.3s',
                '&:hover': { transform: 'translateY(-2px)', boxShadow: theme.shadows[6] }
            }}
        >
            <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                    <Box display="flex" alignItems="center">
                        <MetricIcon color={metric.color} sx={{ mr: 1.5, fontSize: 28 }} />
                        <Typography variant="subtitle1" fontWeight="bold">
                            {metric.label}
                        </Typography>
                    </Box>
                    <Tooltip title="Status Check" placement="top">
                        <Chip 
                            label={metric.status} 
                            color={metric.color} 
                            size="small" 
                            icon={<metric.icon />}
                            sx={{ fontWeight: 'bold' }}
                        />
                    </Tooltip>
                </Box>
                
                <Typography variant="h3" fontWeight={700} color={theme.palette.text.primary} sx={{ mb: 1 }}>
                    {metric.value}
                    <Typography component="span" variant="h5" color="textSecondary" sx={{ ml: 0.5 }}>
                        {metric.unit}
                    </Typography>
                </Typography>

                <LinearProgress 
                    variant="determinate" 
                    value={displayProgress} 
                    color={metric.color} 
                    sx={{ height: 10, borderRadius: 5, mb: 1 }}
                />
                <Box display="flex" justifyContent="space-between">
                    <Typography variant="caption" color="textSecondary">
                        Max: {metric.max}{metric.unit}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                        Value: {metric.value}
                    </Typography>
                </Box>
            </CardContent>
        </Card>
    );
};

// --- MAIN COMPONENT ---
const SystemHealthMonitoring = () => {
    const [metrics, setMetrics] = useState(getLiveMetrics());
    const theme = useTheme();

    // Function to update metrics every 5 seconds
    const updateMetrics = useCallback(() => {
        setMetrics(getLiveMetrics());
    }, []);

    useEffect(() => {
        // Initial fetch
        updateMetrics();

        // Set up interval for real-time simulation
        const intervalId = setInterval(updateMetrics, 5000);

        // Clear interval on component unmount
        return () => clearInterval(intervalId);
    }, [updateMetrics]);

    // Calculate overall health based on the worst status
    const overallHealth = metrics.reduce((worst, current) => {
        if (current.status === 'Critical') return 'Critical';
        if (current.status === 'Warning' && worst !== 'Critical') return 'Warning';
        return worst;
    }, 'Healthy');

    const getHealthProps = (health) => {
        switch (health) {
            case 'Critical': return { color: 'error', label: 'Critical' };
            case 'Warning': return { color: 'warning', label: 'Degraded' };
            default: return { color: 'success', label: 'Optimal' };
        }
    };

    const healthProps = getHealthProps(overallHealth);

    return (
        <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
            <Typography 
                variant="h4" 
                component="h1" 
                gutterBottom 
                sx={{ 
                    fontWeight: 800, 
                    color: theme.palette.primary.main, 
                    display: 'flex',
                    alignItems: 'center',
                    pb: 1
                }}
            >
                <HealthAndSafetyIcon sx={{ mr: 1.5, fontSize: '3rem', color: healthProps.color === 'error' ? theme.palette.error.main : theme.palette.secondary.main }} />
                System Health Monitoring
            </Typography>
            <Typography variant="h6" color="textSecondary" sx={{ mb: 4 }}>
                Real-time, actionable telemetry across core services. Auto-refresh: 5s.
            </Typography>

            <Paper elevation={6} sx={{ p: 3, borderRadius: 2, mb: 4 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="h5" fontWeight={700}>
                        Overall System Status
                    </Typography>
                    <Chip 
                        label={healthProps.label} 
                        color={healthProps.color} 
                        size="medium"
                        sx={{ fontSize: '1rem', height: 'auto', p: 1, fontWeight: 'bold' }}
                    />
                </Box>
                <LinearProgress 
                    variant="determinate" 
                    value={overallHealth === 'Critical' ? 100 : (overallHealth === 'Warning' ? 66 : 33)} 
                    color={healthProps.color} 
                    sx={{ height: 12, borderRadius: 5 }}
                />
            </Paper>

            <Grid container spacing={4}>
                {metrics.map((metric, index) => (
                    <Grid item xs={12} sm={6} lg={3} key={index}>
                        <MetricCard metric={metric} theme={theme} />
                    </Grid>
                ))}
            </Grid>
        </Container>
    );
};

// EXPORT STATEMENT IS CORRECT
export default SystemHealthMonitoring;